import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';
import { PayslipFileModal } from './PayslipFileModal';

function ars(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  });
}

/** Acepta 999256.28, 999256,28, 999.256,28 o 999,256.28 */
function parseMoneyInput(raw: string): number | undefined {
  const s = raw.trim().replace(/[^\d.,\-]/g, '');
  if (!s) return undefined;
  let normalized = s;
  if (normalized.includes(',') && normalized.includes('.')) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (normalized.includes(',')) {
    const parts = normalized.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      normalized = `${parts[0].replace(/\./g, '')}.${parts[1]}`;
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : undefined;
}

function apiErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string') return msg;
  return err?.message ?? 'Error desconocido';
}

function Dropzone({
  label,
  hint,
  multiple,
  busy,
  onFiles,
}: {
  label: string;
  hint: string;
  multiple?: boolean;
  busy?: boolean;
  onFiles: (files: File[]) => void;
}) {
  return (
    <label className="block border border-dashed rounded-md p-3 cursor-pointer hover:bg-gray-50">
      <p className="text-sm font-medium text-gray-800">{label}</p>
      <p className="text-xs text-gray-500">{busy ? 'Subiendo…' : hint}</p>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        multiple={multiple}
        className="sr-only"
        disabled={busy}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </label>
  );
}

export function CustomerOriginationDocs({
  customerId,
  customerDocumentNumber,
  onChanged,
}: {
  customerId: string;
  customerDocumentNumber?: string;
  onChanged?: () => Promise<void> | void;
}) {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [dniOpen, setDniOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.listOriginationDocuments(customerId);
      setDocs(list);
    } catch (e: any) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const payslips = docs.filter((d) => d.kind === 'PAYSLIP');
  const front = docs.find((d) => d.kind === 'DNI_FRONT') || docs.find((d) => d.kind === 'DNI') || null;
  const back = docs.find((d) => d.kind === 'DNI_BACK') || null;
  const hasDni = Boolean(front || back);

  const onUpload = async (kind: 'PAYSLIP' | 'DNI_FRONT' | 'DNI_BACK', file?: File) => {
    if (!file) return;
    setUploading(kind);
    setError(null);
    try {
      const saved = await apiService.uploadOriginationDocument(customerId, kind, file);
      if (saved?.id) {
        try {
          await apiService.extractOriginationDocument(saved.id);
        } catch (ocrErr) {
          setError(`Archivo guardado, pero OCR no pudo completarse: ${apiErrorMessage(ocrErr)}`);
        }
      }
      await load();
      await onChanged?.();
      if (kind === 'DNI_FRONT' || kind === 'DNI_BACK') setDniOpen(true);
    } catch (e: unknown) {
      setError(apiErrorMessage(e));
    } finally {
      setUploading(null);
    }
  };

  const confirmPayslip = async (doc: any, patch: Record<string, unknown>) => {
    setBusyId(doc.id);
    setError(null);
    try {
      await apiService.confirmOriginationDocument(doc.id, patch);
      await load();
      await onChanged?.();
    } catch (e: unknown) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:col-span-2">
      <h3 className="text-sm font-semibold text-gray-700 mb-1 uppercase">Documentos de originación</h3>
      <p className="text-xs text-gray-500 mb-3">
        Cargá recibos y DNI (frente/dorso). Se corre OCR automáticamente; después confirmá los datos.
      </p>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {loading ? (
        <p className="text-sm text-gray-500">Cargando documentos…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase">Recibos</p>
            <Dropzone
              label="Recibos de sueldo"
              hint="PDF o foto. Podés subir varios."
              multiple
              busy={uploading === 'PAYSLIP'}
              onFiles={(files) => files.forEach((f) => void onUpload('PAYSLIP', f))}
            />
            {payslips.length === 0 ? (
              <p className="text-sm text-gray-500">Todavía no hay recibos cargados.</p>
            ) : (
              <div className="space-y-3">
                {payslips.map((doc) => (
                  <PayslipCard
                    key={doc.id}
                    doc={doc}
                    busy={busyId === doc.id}
                    onConfirm={confirmPayslip}
                    onExtract={async () => {
                      setBusyId(doc.id);
                      try {
                        await apiService.extractOriginationDocument(doc.id);
                        await load();
                      } catch (e: unknown) {
                        setError(apiErrorMessage(e));
                      } finally {
                        setBusyId(null);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase">DNI</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Dropzone
                label="DNI frente"
                hint={front ? front.originalName : 'Foto o PDF'}
                busy={uploading === 'DNI_FRONT'}
                onFiles={(files) => void onUpload('DNI_FRONT', files[0])}
              />
              <Dropzone
                label="DNI dorso"
                hint={back ? back.originalName : 'Foto o PDF'}
                busy={uploading === 'DNI_BACK'}
                onFiles={(files) => void onUpload('DNI_BACK', files[0])}
              />
            </div>
            {hasDni ? (
              <div className="space-y-1 text-sm text-gray-700">
                {front && (
                  <p>
                    Frente: {front.originalName}
                    {front.confirmedDniNumber ? ` · ${front.confirmedDniNumber}` : ''}
                  </p>
                )}
                {back && <p>Dorso: {back.originalName}</p>}
                <button
                  type="button"
                  className="text-blue-700 hover:underline text-sm"
                  onClick={() => setDniOpen(true)}
                >
                  Ver DNI + OCR
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Subí frente y dorso para validar con OCR.</p>
            )}
          </div>
        </div>
      )}

      {dniOpen && hasDni && (
        <DniOcrModal
          customerDocumentNumber={customerDocumentNumber}
          front={front}
          back={back}
          onClose={() => setDniOpen(false)}
          onConfirm={async (patch) => {
            const target = front || back;
            if (!target) return;
            await apiService.confirmOriginationDocument(target.id, patch);
            await load();
            await onChanged?.();
            setDniOpen(false);
          }}
          onExtract={async (id) => {
            setBusyId(id);
            try {
              await apiService.extractOriginationDocument(id);
              await load();
            } catch (e: unknown) {
              setError(apiErrorMessage(e));
            } finally {
              setBusyId(null);
            }
          }}
          extracting={Boolean(busyId)}
        />
      )}
    </div>
  );
}

function PayslipCard({
  doc,
  busy,
  onConfirm,
  onExtract,
}: {
  doc: any;
  busy?: boolean;
  onConfirm: (doc: any, patch: Record<string, unknown>) => Promise<void>;
  onExtract: () => Promise<void>;
}) {
  const [net, setNet] = useState(doc.confirmedNet != null ? String(doc.confirmedNet) : '');
  const [period, setPeriod] = useState(doc.confirmedPeriod ?? '');
  const [payday, setPayday] = useState(doc.confirmedPayday ?? '');
  const [deductions, setDeductions] = useState(doc.confirmedDeductions ?? '');
  const [viewOpen, setViewOpen] = useState(false);

  useEffect(() => {
    setNet(doc.confirmedNet != null ? String(doc.confirmedNet) : '');
    setPeriod(doc.confirmedPeriod ?? '');
    setPayday(doc.confirmedPayday ?? '');
    setDeductions(doc.confirmedDeductions ?? '');
  }, [doc.id, doc.confirmedNet, doc.confirmedPeriod, doc.confirmedPayday, doc.confirmedDeductions]);

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex justify-between gap-2 text-xs text-gray-600">
        <span className="truncate">{doc.originalName}</span>
        <span>{doc.confirmedAt ? 'Confirmado' : 'Pendiente'}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={net} onChange={(e) => setNet(e.target.value)} placeholder="Neto" className="px-2 py-1 border rounded text-sm" />
        <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Período" className="px-2 py-1 border rounded text-sm" />
        <input value={payday} onChange={(e) => setPayday(e.target.value)} placeholder="Día cobro" className="px-2 py-1 border rounded text-sm" />
        <input value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="Descuentos" className="px-2 py-1 border rounded text-sm" />
      </div>
      <p className="text-xs text-gray-500">Neto sugerido: {ars(doc.confirmedNet)}</p>
      <div className="flex flex-wrap gap-3">
        <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setViewOpen(true)}>
          Ver recibo
        </button>
        <button type="button" disabled={busy} className="text-xs text-blue-700 hover:underline disabled:opacity-50" onClick={onExtract}>
          Extraer con IA
        </button>
        <button
          type="button"
          disabled={busy}
          className="text-xs text-blue-700 hover:underline disabled:opacity-50"
          onClick={() =>
            onConfirm(doc, {
              confirmedNet: net ? parseMoneyInput(net) : undefined,
              confirmedPeriod: period || undefined,
              confirmedPayday: payday || undefined,
              confirmedDeductions: deductions || undefined,
            })
          }
        >
          Confirmar (completa ingreso)
        </button>
      </div>
      {viewOpen && <PayslipFileModal doc={doc} onClose={() => setViewOpen(false)} />}
    </div>
  );
}

function DniOcrModal({
  customerDocumentNumber,
  front,
  back,
  onClose,
  onConfirm,
  onExtract,
  extracting,
}: {
  customerDocumentNumber?: string;
  front: any;
  back: any;
  onClose: () => void;
  onConfirm: (patch: Record<string, unknown>) => Promise<void>;
  onExtract: (id: string) => Promise<void>;
  extracting?: boolean;
}) {
  const [dniNumber, setDniNumber] = useState(front?.confirmedDniNumber || back?.confirmedDniNumber || '');
  const [firstName, setFirstName] = useState(front?.confirmedFirstName || back?.confirmedFirstName || '');
  const [lastName, setLastName] = useState(front?.confirmedLastName || back?.confirmedLastName || '');
  const [expiry, setExpiry] = useState(front?.confirmedExpiry || back?.confirmedExpiry || '');
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDniNumber(front?.confirmedDniNumber || back?.confirmedDniNumber || '');
    setFirstName(front?.confirmedFirstName || back?.confirmedFirstName || '');
    setLastName(front?.confirmedLastName || back?.confirmedLastName || '');
    setExpiry(front?.confirmedExpiry || back?.confirmedExpiry || '');
  }, [
    front?.id,
    back?.id,
    front?.confirmedDniNumber,
    front?.confirmedFirstName,
    front?.confirmedLastName,
    front?.confirmedExpiry,
    back?.confirmedDniNumber,
    back?.confirmedFirstName,
    back?.confirmedLastName,
    back?.confirmedExpiry,
  ]);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    const load = async () => {
      if (front?.id) {
        const blob = await apiService.getOriginationDocumentFileBlob(front.id);
        const url = URL.createObjectURL(blob);
        urls.push(url);
        if (!cancelled) setFrontUrl(url);
      } else if (!cancelled) setFrontUrl(null);
      if (back?.id) {
        const blob = await apiService.getOriginationDocumentFileBlob(back.id);
        const url = URL.createObjectURL(blob);
        urls.push(url);
        if (!cancelled) setBackUrl(url);
      } else if (!cancelled) setBackUrl(null);
    };
    load().catch(() => undefined);
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [front?.id, back?.id]);

  const mismatch =
    dniNumber &&
    customerDocumentNumber &&
    String(dniNumber).replace(/\D/g, '') !== String(customerDocumentNumber).replace(/\D/g, '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">DNI · fotos y OCR</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-sm">
            Cerrar
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Preview label="Frente" url={frontUrl} mimeType={front?.mimeType} name={front?.originalName} />
            <Preview label="Dorso" url={backUrl} mimeType={back?.mimeType} name={back?.originalName} />
          </div>
          <div className="space-y-3">
            <input value={dniNumber} onChange={(e) => setDniNumber(e.target.value)} placeholder="Nº documento" className="w-full px-3 py-2 border rounded-md text-sm" />
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Apellido" className="w-full px-3 py-2 border rounded-md text-sm" />
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Nombre" className="w-full px-3 py-2 border rounded-md text-sm" />
            <input value={expiry} onChange={(e) => setExpiry(e.target.value)} placeholder="Vencimiento" className="w-full px-3 py-2 border rounded-md text-sm" />
            {mismatch && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                El Nº del OCR no coincide con el DNI del cliente ({customerDocumentNumber}).
              </p>
            )}
            <div className="flex flex-wrap gap-3 pt-2">
              {front?.id && (
                <button type="button" disabled={extracting} className="text-sm text-blue-700 hover:underline disabled:opacity-50" onClick={() => onExtract(front.id)}>
                  Re-OCR frente
                </button>
              )}
              {back?.id && (
                <button type="button" disabled={extracting} className="text-sm text-blue-700 hover:underline disabled:opacity-50" onClick={() => onExtract(back.id)}>
                  Re-OCR dorso
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                className="ml-auto px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await onConfirm({
                      confirmedDniNumber: dniNumber || undefined,
                      confirmedFirstName: firstName || undefined,
                      confirmedLastName: lastName || undefined,
                      confirmedExpiry: expiry || undefined,
                    });
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Confirmar datos del DNI
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Preview({
  label,
  url,
  mimeType,
  name,
}: {
  label: string;
  url: string | null;
  mimeType?: string;
  name?: string;
}) {
  return (
    <div className="border rounded-md p-2 bg-gray-50">
      <p className="text-xs font-medium text-gray-700 mb-2">
        {label}
        {name ? ` · ${name}` : ''}
      </p>
      {!url ? (
        <p className="text-xs text-gray-500 py-8 text-center">Sin imagen</p>
      ) : mimeType?.startsWith('image/') ? (
        <img src={url} alt={label} className="max-h-56 w-full object-contain rounded" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline">
          Abrir archivo
        </a>
      )}
    </div>
  );
}
